import React, {PureComponent} from "react";
import withStyles from '@mui/styles/withStyles';
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Chip from "@mui/material/Chip";
import moment from "moment";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {ExerciseType} from "../../../core/models/ExerciseType";

const useStyles = theme => ({
    summaryCard: {
        marginBottom: theme.spacing(2),
        borderRadius: 12,
    },
    statRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0.5, 0),
    },
    statLabel: {
        color: theme.palette.text.secondary,
    },
    positive: {
        color: '#4caf50',
        fontWeight: 600,
    },
    negative: {
        color: '#f44336',
        fontWeight: 600,
    },
    neutral: {
        color: theme.palette.text.secondary,
        fontWeight: 600,
    },
    exerciseCard: {
        marginBottom: theme.spacing(1.5),
        borderRadius: 8,
    },
    exerciseHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    setRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.25, 0),
    },
    setNumber: {
        minWidth: 20,
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
    },
    previousValue: {
        color: theme.palette.text.disabled,
        fontSize: '0.8rem',
        textDecoration: 'line-through',
    },
    currentValue: {
        fontSize: '0.875rem',
    },
    deltaChip: {
        height: 20,
        fontSize: '0.7rem',
    },
    newBadge: {
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
    },
    skippedBadge: {
        backgroundColor: '#fff3e0',
        color: '#e65100',
    },
    est1rmRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
    },
});

class WorkoutComparisonTab extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            comparison: null,
            isLoading: true,
            hasError: false,
        };
    }

    componentDidMount() {
        this.fetchComparison();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.workoutId !== this.props.workoutId) {
            this.fetchComparison(nextProps.workoutId);
        }
    }

    fetchComparison = (workoutId = null) => {
        const id = workoutId || this.props.workoutId;

        this.setState({isLoading: true, hasError: false});

        fetch(UrlBuilder.workout.workoutComparisonApi(id))
            .then(res => res.json())
            .then(res => {
                if (res.data) {
                    this.setState({
                        comparison: res.data,
                        isLoading: false,
                    });
                } else {
                    this.setState({
                        isLoading: false,
                        hasError: true,
                    });
                }
            })
            .catch(() => {
                this.setState({
                    isLoading: false,
                    hasError: true,
                });
            });
    }

    getDeltaClass = (value) => {
        const {classes} = this.props;
        if (value > 0) return classes.positive;
        if (value < 0) return classes.negative;
        return classes.neutral;
    }

    formatDelta = (value, suffix = '') => {
        if (value === null || value === undefined) return null;
        const sign = value > 0 ? '+' : '';
        return `${sign}${Math.round(value * 10) / 10}${suffix}`;
    }

    getDeltaIcon = (value) => {
        if (value > 0) return <TrendingUpIcon sx={{fontSize: 16, color: '#4caf50'}}/>;
        if (value < 0) return <TrendingDownIcon sx={{fontSize: 16, color: '#f44336'}}/>;
        return <TrendingFlatIcon sx={{fontSize: 16, color: 'text.secondary'}}/>;
    }

    formatSetValues = (set, exerciseTypeId) => {
        if (!set || !set.values) return '';

        switch (exerciseTypeId) {
            case ExerciseType.WeightAndReps:
                return `${set.values[0]}kg x ${set.values[1]} reps`;
            case ExerciseType.Reps:
                return `${set.values[0]} reps`;
            case ExerciseType.DistanceAndTime:
                return `${set.values[0]}km x ${set.values[1]}min`;
            case ExerciseType.Time:
                return `${set.values[0]}min`;
            case ExerciseType.WeightAndTime:
                return `${set.values[0]}kg x ${set.values[1]}min`;
            default:
                return set.values.join(' x ');
        }
    }

    renderSummaryCard = () => {
        const {classes} = this.props;
        const {comparison} = this.state;
        const {summary, previousWorkout} = comparison;

        return (
            <Card variant="outlined" className={classes.summaryCard}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography variant="subtitle2" color="textSecondary">
                            vs. {moment(previousWorkout.dateTimeStarted).format('MMM D')} session
                        </Typography>
                        {this.getDeltaIcon(summary.volumeChangePercent)}
                    </Box>

                    <div className={classes.statRow}>
                        <Typography variant="body2" className={classes.statLabel}>Total Volume</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2">
                                {Math.round(summary.currentVolume).toLocaleString()}kg
                            </Typography>
                            <Typography variant="caption" className={this.getDeltaClass(summary.volumeChangePercent)}>
                                {this.formatDelta(summary.volumeChangePercent, '%')}
                            </Typography>
                        </Box>
                    </div>

                    <div className={classes.statRow}>
                        <Typography variant="body2" className={classes.statLabel}>Duration</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2">
                                {summary.currentDurationMinutes}min
                            </Typography>
                            <Typography variant="caption" className={this.getDeltaClass(summary.previousDurationMinutes - summary.currentDurationMinutes)}>
                                {summary.currentDurationMinutes !== summary.previousDurationMinutes
                                    ? this.formatDelta(summary.currentDurationMinutes - summary.previousDurationMinutes, 'min')
                                    : null
                                }
                            </Typography>
                        </Box>
                    </div>

                    <div className={classes.statRow}>
                        <Typography variant="body2" className={classes.statLabel}>Sets</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2">
                                {summary.currentSetCount}
                            </Typography>
                            {summary.currentSetCount !== summary.previousSetCount &&
                                <Typography variant="caption" className={this.getDeltaClass(summary.currentSetCount - summary.previousSetCount)}>
                                    {this.formatDelta(summary.currentSetCount - summary.previousSetCount)}
                                </Typography>
                            }
                        </Box>
                    </div>

                    {summary.currentAvgRpe != null && summary.previousAvgRpe != null &&
                        <div className={classes.statRow}>
                            <Typography variant="body2" className={classes.statLabel}>Avg RPE</Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="body2">
                                    {summary.currentAvgRpe}
                                </Typography>
                                <Typography variant="caption" className={this.getDeltaClass(summary.currentAvgRpe - summary.previousAvgRpe)}>
                                    {this.formatDelta(summary.currentAvgRpe - summary.previousAvgRpe)}
                                </Typography>
                            </Box>
                        </div>
                    }

                    <div className={classes.statRow}>
                        <Typography variant="body2" className={classes.statLabel}>Exercises</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2">
                                {summary.currentExerciseCount}
                            </Typography>
                            {summary.currentExerciseCount !== summary.previousExerciseCount &&
                                <Typography variant="caption" className={this.getDeltaClass(summary.currentExerciseCount - summary.previousExerciseCount)}>
                                    {this.formatDelta(summary.currentExerciseCount - summary.previousExerciseCount)}
                                </Typography>
                            }
                        </Box>
                    </div>
                </CardContent>
            </Card>
        );
    }

    renderExerciseComparison = (exercise) => {
        const {classes} = this.props;
        const maxSets = Math.max(
            exercise.currentSets ? exercise.currentSets.length : 0,
            exercise.previousSets ? exercise.previousSets.length : 0
        );

        return (
            <Card variant="outlined" className={classes.exerciseCard} key={exercise.exerciseId}>
                <CardContent sx={{padding: 1.5, '&:last-child': {paddingBottom: 1.5}}}>
                    <div className={classes.exerciseHeader}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <FitnessCenterIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                            <Typography variant="subtitle2">
                                {exercise.exerciseName}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {exercise.status === 'new' &&
                                <Chip label="New" size="small" className={`${classes.deltaChip} ${classes.newBadge}`}/>
                            }
                            {exercise.status === 'skipped' &&
                                <Chip label="Skipped" size="small" className={`${classes.deltaChip} ${classes.skippedBadge}`}/>
                            }
                            {exercise.status === 'matched' && exercise.volumeChangePercent !== null &&
                                <Chip
                                    label={this.formatDelta(exercise.volumeChangePercent, '%')}
                                    size="small"
                                    className={classes.deltaChip}
                                    sx={{
                                        backgroundColor: exercise.volumeChangePercent > 0 ? '#e8f5e9' : exercise.volumeChangePercent < 0 ? '#ffebee' : '#f5f5f5',
                                        color: exercise.volumeChangePercent > 0 ? '#2e7d32' : exercise.volumeChangePercent < 0 ? '#c62828' : '#757575',
                                    }}
                                />
                            }
                        </Box>
                    </div>

                    {exercise.status === 'matched' && maxSets > 0 &&
                        <Box mt={1}>
                            {Array.from({length: maxSets}, (_, idx) => {
                                const currentSet = exercise.currentSets[idx];
                                const previousSet = exercise.previousSets[idx];

                                return (
                                    <div className={classes.setRow} key={idx}>
                                        <Typography className={classes.setNumber}>
                                            {idx + 1}
                                        </Typography>
                                        {previousSet && currentSet &&
                                            <Typography className={classes.previousValue}>
                                                {this.formatSetValues(previousSet, exercise.exerciseTypeId)}
                                            </Typography>
                                        }
                                        {previousSet && currentSet &&
                                            <Typography variant="body2" sx={{color: 'text.secondary', fontSize: '0.8rem'}}>
                                                →
                                            </Typography>
                                        }
                                        {currentSet &&
                                            <Typography className={classes.currentValue}>
                                                {this.formatSetValues(currentSet, exercise.exerciseTypeId)}
                                            </Typography>
                                        }
                                        {!currentSet && previousSet &&
                                            <Typography className={classes.previousValue}>
                                                {this.formatSetValues(previousSet, exercise.exerciseTypeId)} (dropped)
                                            </Typography>
                                        }
                                        {currentSet && !previousSet &&
                                            <>
                                                <Typography className={classes.currentValue}>
                                                    {this.formatSetValues(currentSet, exercise.exerciseTypeId)}
                                                </Typography>
                                                <Chip label="extra set" size="small" className={classes.deltaChip} sx={{backgroundColor: '#e8f5e9', color: '#2e7d32'}}/>
                                            </>
                                        }
                                    </div>
                                );
                            })}
                        </Box>
                    }

                    {exercise.status === 'new' && exercise.currentSets.length > 0 &&
                        <Box mt={0.5}>
                            <Typography variant="caption" color="textSecondary">
                                {exercise.currentSets.length} set{exercise.currentSets.length > 1 ? 's' : ''} logged
                            </Typography>
                        </Box>
                    }

                    {exercise.status === 'skipped' && exercise.previousSets.length > 0 &&
                        <Box mt={0.5}>
                            <Typography variant="caption" color="textSecondary">
                                Was {exercise.previousSets.length} set{exercise.previousSets.length > 1 ? 's' : ''} last time
                            </Typography>
                        </Box>
                    }

                    {exercise.currentEst1RM && exercise.previousEst1RM &&
                        <div className={classes.est1rmRow}>
                            <Typography variant="caption" color="textSecondary">
                                Est. 1RM:
                            </Typography>
                            <Typography variant="caption" className={this.getDeltaClass(exercise.currentEst1RM - exercise.previousEst1RM)}>
                                {exercise.currentEst1RM}kg
                                {exercise.currentEst1RM !== exercise.previousEst1RM &&
                                    ` (${this.formatDelta(exercise.currentEst1RM - exercise.previousEst1RM)}kg)`
                                }
                            </Typography>
                        </div>
                    }
                </CardContent>
            </Card>
        );
    }

    render() {
        const {classes} = this.props;
        const {comparison, isLoading, hasError} = this.state;

        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28}/>
                </Box>
            );
        }

        if (hasError || !comparison) {
            return (
                <div className={classes.emptyState}>
                    <Typography variant="body2" color="textSecondary">
                        Complete this routine again to see your progress comparison.
                    </Typography>
                </div>
            );
        }

        return (
            <>
                {this.renderSummaryCard()}

                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Exercise Breakdown
                </Typography>

                {comparison.exerciseComparisons.map(exercise =>
                    this.renderExerciseComparison(exercise)
                )}
            </>
        );
    }
}

export default withStyles(useStyles)(WorkoutComparisonTab);
